import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, TableShell, EmptyRow } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const empty = { name: "", glass_type: "", description: "", price: "" };

export default function Services() {
  const { t, fmt } = useApp();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/services").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editId) await api.put(`/services/${editId}`, payload);
      else await api.post("/services", payload);
      toast.success(t("saved")); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/services/${id}`); toast.success(t("deleted")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHeader title={t("services")}>
        <Button data-testid="add-service-button" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}
          className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
          <Plus size={18} className="mr-2" /> {t("addService")}
        </Button>
      </PageHeader>
      <TableShell headers={[t("name"), t("glassType"), t("description"), t("price"), t("actions")]}>
        {rows.length === 0 && <EmptyRow colSpan={5} />}
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-semibold text-slate-900">{r.name}</td>
            <td className="px-4 py-3">{r.glass_type}</td>
            <td className="px-4 py-3 text-slate-500">{r.description}</td>
            <td className="px-4 py-3 font-medium">{fmt(r.price)}</td>
            <td className="px-4 py-3">
              <div className="flex gap-1">
                <Button data-testid={`edit-service-${r.id}`} variant="ghost" size="icon"
                  onClick={() => { setForm({ ...r, price: String(r.price) }); setEditId(r.id); setOpen(true); }}>
                  <Pencil size={15} />
                </Button>
                <Button data-testid={`delete-service-${r.id}`} variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => remove(r.id)}>
                  <Trash2 size={15} />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </TableShell>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editId ? t("editService") : t("addService")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("name")} *</Label>
              <Input data-testid="service-name-input" required value={form.name} onChange={set("name")} className="h-11" placeholder="Windshield Replacement" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("glassType")}</Label>
              <Input data-testid="service-glass-type-input" value={form.glass_type} onChange={set("glass_type")} className="h-11" placeholder="Windshield / Door glass / Rear" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("description")}</Label>
              <Input data-testid="service-description-input" value={form.description} onChange={set("description")} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("price")} *</Label>
              <Input data-testid="service-price-input" required type="number" step="0.01" min="0" value={form.price} onChange={set("price")} className="h-11" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="service-save-button" type="submit" className="h-11 bg-sky-600 hover:bg-sky-700 text-white">{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
